import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Avatar,
  Rating,
  Dialog,
  TextField,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Divider,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import StarIcon from '@mui/icons-material/Star';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import api from '../services/api';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const PublicCompanyPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [company, setCompany] = useState<any>(null);
  const [showBookingModal, setShowBookingModal] = useState(false);

  const brandColor = '#c9a227';

  // Fetch company data
  useEffect(() => {
    const fetchCompany = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/public/${slug}`);
        setCompany(response.data);
      } catch (err: any) {
        setError(err.response?.data?.error || 'Barbearia não encontrada');
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchCompany();
    }
  }, [slug]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !company) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error">{error || 'Barbearia não encontrada'}</Alert>
      </Container>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          background: `linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)`,
          color: 'white',
          py: 6,
        }}
      >
        <Container>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '2fr 1fr' }, gap: 3, alignItems: 'center' }}>
            <Box>
              <Typography variant="h3" component="h1" sx={{ fontWeight: 'bold', mb: 2 }}>
                {company.name}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                {company.avgRating && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Rating
                      value={parseFloat(company.avgRating)}
                      precision={0.1}
                      readOnly
                      sx={{ color: brandColor }}
                    />
                    <Typography variant="body2">
                      ({company.reviewCount} avaliações)
                    </Typography>
                  </Box>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {company.phone && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PhoneIcon />
                    <Typography variant="body2">{company.phone}</Typography>
                  </Box>
                )}
                {company.address && company.settings?.show_address && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocationOnIcon />
                    <Typography variant="body2">{company.address}</Typography>
                  </Box>
                )}
              </Box>
            </Box>
            <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
              <Button
                variant="contained"
                size="large"
                onClick={() => setShowBookingModal(true)}
                sx={{
                  backgroundColor: brandColor,
                  color: 'white',
                  '&:hover': { backgroundColor: '#a87e1a' },
                }}
              >
                Agendar Agora
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Tabs Content */}
      <Container sx={{ py: 4 }}>
        <Tabs
          value={tabValue}
          onChange={(e, newValue) => setTabValue(newValue)}
          sx={{ mb: 3, borderBottom: '1px solid #eee' }}
        >
          <Tab label="Profissionais" id="tab-0" aria-controls="tabpanel-0" />
          <Tab label="Serviços" id="tab-1" aria-controls="tabpanel-1" />
          <Tab label="Avaliações" id="tab-2" aria-controls="tabpanel-2" />
        </Tabs>

        {/* Profissionais */}
        <TabPanel value={tabValue} index={0}>
          <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3 }}>
            Nossa Equipe
          </Typography>
          {company.professionals && company.professionals.length > 0 ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 3 }}>
              {company.professionals.map((prof: any) => (
                <Card key={prof.id} sx={{ textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                  <CardContent>
                    <Avatar
                      src={prof.photo_url}
                      sx={{
                        width: 120,
                        height: 120,
                        margin: '0 auto 16px',
                        backgroundColor: brandColor,
                      }}
                    >
                      {prof.name.charAt(0)}
                    </Avatar>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      {prof.name}
                    </Typography>
                    {prof.specialization && (
                      <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>
                        {prof.specialization}
                      </Typography>
                    )}
                    {prof.bio && (
                      <Typography variant="body2" color="textSecondary">
                        {prof.bio}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : (
            <Typography color="textSecondary">Nenhum profissional cadastrado</Typography>
          )}
        </TabPanel>

        {/* Serviços */}
        <TabPanel value={tabValue} index={1}>
          <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3 }}>
            Serviços Disponíveis
          </Typography>
          {company.services && company.services.length > 0 ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 2 }}>
              {company.services.map((service: any) => (
                <Card key={service.id} sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {service.name}
                    </Typography>
                    {service.description && (
                      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                        {service.description}
                      </Typography>
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <AccessTimeIcon sx={{ fontSize: '1rem' }} />
                        <Typography variant="caption">{service.duration} min</Typography>
                      </Box>
                      <Typography
                        variant="h6"
                        sx={{ color: brandColor, fontWeight: 'bold' }}
                      >
                        R$ {(service.price / 100).toFixed(2)}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : (
            <Typography color="textSecondary">Nenhum serviço cadastrado</Typography>
          )}
        </TabPanel>

        {/* Avaliações */}
        <TabPanel value={tabValue} index={2}>
          <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3 }}>
            Avaliações e Comentários
          </Typography>
          {company.reviews && company.reviews.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {company.reviews.map((review: any) => (
                <Card key={review.id} sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                        {review.customer_name}
                      </Typography>
                      <Rating value={review.rating} readOnly size="small" sx={{ color: brandColor }} />
                    </Box>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                      {review.comment}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {new Date(review.created_at).toLocaleDateString('pt-BR')}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : (
            <Typography color="textSecondary">Nenhuma avaliação ainda</Typography>
          )}
        </TabPanel>
      </Container>

      {/* Horário de Funcionamento */}
      <Box sx={{ backgroundColor: '#f5f5f5', py: 4 }}>
        <Container>
          <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3 }}>
            Horário de Funcionamento
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 2 }}>
            {company.settings?.working_hours &&
              Object.entries(company.settings.working_hours).map(([day, hours]: [string, any]) => (
                <Box key={day} sx={{ p: 2, backgroundColor: 'white', borderRadius: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', textTransform: 'capitalize' }}>
                    {day}
                  </Typography>
                  <Typography variant="body2">
                    {hours.open && hours.close
                      ? `${hours.open} - ${hours.close}`
                      : 'Fechado'}
                  </Typography>
                </Box>
              ))}
          </Box>
        </Container>
      </Box>

      {/* Booking Modal */}
      <Dialog
        open={showBookingModal}
        onClose={() => setShowBookingModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
            Agendar em {company.name}
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            Você será redirecionado para a página de agendamento.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => setShowBookingModal(false)}
            >
              Cancelar
            </Button>
            <Button
              fullWidth
              variant="contained"
              sx={{
                backgroundColor: brandColor,
                color: 'white',
                '&:hover': { backgroundColor: '#a87e1a' },
              }}
              onClick={() => {
                setShowBookingModal(false);
                // Implement booking flow
              }}
            >
              Continuar
            </Button>
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
};

export default PublicCompanyPage;
